import { useEffect, useRef, useState } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'

import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE, UserUnitSettings } from '../../common/constants'
import { CollapsibleSection } from '../CollapsibleSection'
import { formatDateForChart } from '../../common/utils'
import { FlowMeterChartSubHeader } from '../FlowMeterChartSubHeader'
import { DeviceInfo, SensorProduct, UnitCategoryId } from '../../common/enums'
import { convertBaseToDisplay } from '../../common/conversions'
import { Viewport } from '../../common/types'
import { api } from '../../services'

const { CanvasJSChart } = CanvasJSReact

const UNIT_ID_ACRE_FOOT = 3 // Acre-Foot
// const DEFAULT_VOLUME_RATE_UNITS = 6 // Gallon per minute

export interface FlowMeterSettings {
  volume: {
    unitId: number
    precision: number
  }
  volumeRate: {
    unitId: number
    precision: number
  }
}

interface DataPoints {
  volume: { x: number; y?: number }[]
  totals: { x: number; y?: number }[]
  rate: { x: number; y?: number }[]
}

interface Props {
  device: DeviceInfo
  sigfoxId: string
  isLoading: boolean
  sensorPort: string
  sensorProduct: SensorProduct
  sensorReadings?: any[] // { metadata: any; readings: any } []

  viewport: Viewport

  currentTime: number
  userUnitSettings: UserUnitSettings

  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
}

export function FlowMeterChart({
  device,
  sigfoxId,
  isLoading,
  sensorPort,
  sensorReadings,
  sensorProduct,
  viewport,
  userUnitSettings,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = (timestamp: number) => {},
}: Props) {
  const ref = useRef(null)

  const [showVolume, setShowVolume] = useState(true)
  const [showRate, setShowRate] = useState(true)
  const [showTotals, setShowTotals] = useState(true)

  const [x, setX] = useState(0)
  const [y, setY] = useState(0)
  const [diameter, setDiameter] = useState<number>(0)
  let trackDay = -1

  useEffect(() => {
    if (ref === null) return

    const canvas = document.getElementsByClassName('canvasjs-chart-canvas')[1]
    canvas.addEventListener('click', onClickTest)

    function onClickTest() {
      addTimestamp(x)
    }

    return () => {
      canvas.removeEventListener('click', onClickTest)
    }
  }, [ref, x, y]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasReadings || !sensorReadings) return

    const timestamps = Object.keys(sensorReadings[0].readings)
    const lastReading = Number(timestamps[timestamps.length - 1])

    try {
      api.getSensorConfig(sigfoxId, Math.floor(lastReading)).then((result) => {
        if (result && result.data && result.data[sensorPort]) {
          const { conversionOffset } = result.data[sensorPort]
          setDiameter(conversionOffset || 0)
        }
      })
    } catch (error) {
      console.error({ error })
    }
  }, [sigfoxId, sensorPort, sensorReadings]) // eslint-disable-line react-hooks/exhaustive-deps

  const settings = {
    volume: userUnitSettings[UnitCategoryId.Volume],
    volumeRate: userUnitSettings[UnitCategoryId.VolumeRate],
  }

  let volumeConversions: any = null
  let volumeRateConversions: any = null

  let maxVolume = 0
  let maxRate = 0
  let maxTotals = 0

  const hasReadings = checkForReadings()
  const titleFontSize = 16
  const labelFontSize = 16
  const { dataPoints, totalVolume, totalVolumeInAcreFoot } = generateDataPoints()

  function generateDataPoints() {
    const start = new Date()

    const dataPoints: DataPoints = {
      volume: [],
      totals: [],
      rate: [],
    }

    let volume = 0
    let totalVolume = 0
    let totalVolumeInAcreFoot = 0

    // add data points to get the full range of the viewport
    dataPoints.volume.push({ x: viewport.minimum, y: undefined })
    dataPoints.totals.push({ x: viewport.minimum, y: undefined })
    dataPoints.rate.push({ x: viewport.minimum, y: undefined })

    if (hasReadings && sensorReadings && sensorReadings.length > 0) {
      const volumeData = sensorReadings.filter(
        (reading: any) => reading.metadata.units.category.longName === 'Volume'
      )[0]

      const conversionFormulas = volumeData.metadata.units.conversions.formulas
      volumeConversions = volumeData.metadata.units.conversions.abbreviations

      Object.keys(volumeData.readings).forEach((ts: any) => {
        const timestamp = Number(ts) * 1000

        volume = convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[settings.volume.unitId])
        if (timestamp >= viewport.minimum && timestamp <= viewport.maximum) {
          totalVolume += volume
          if (totalVolume > maxTotals) maxTotals = totalVolume
          if (volume > maxVolume) maxVolume = volume

          dataPoints.volume.push({ x: timestamp, y: volume })
          dataPoints.totals.push({ x: timestamp, y: totalVolume })

          totalVolumeInAcreFoot += convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[UNIT_ID_ACRE_FOOT])
        }
      })
    }

    if (hasReadings && sensorReadings && sensorReadings.length > 0) {
      const volumeRateData = sensorReadings.filter(
        (reading: any) => reading.metadata.units.category.longName === 'Volume Rate'
      )[0]

      const conversionFormulas = volumeRateData.metadata.units.conversions.formulas
      volumeRateConversions = volumeRateData.metadata.units.conversions.abbreviations

      let lastTimestamp = 0

      Object.keys(volumeRateData.readings).forEach((ts: any) => {
        const timestamp = Number(ts) * 1000

        // this is to fix anomalies in the chart (when there is 20min gap between readings)
        if (timestamp - lastTimestamp > 20 * 60 * 1000) {
          dataPoints.rate.push({ x: timestamp - 60, y: undefined })
        }

        if (timestamp >= viewport.minimum && timestamp <= viewport.maximum) {
          const rate = convertBaseToDisplay(volumeRateData.readings[ts], conversionFormulas[settings.volumeRate.unitId])

          if (rate > maxRate) maxRate = rate

          dataPoints.rate.push({ x: timestamp, y: rate })
        }

        lastTimestamp = timestamp
      })
    }

    dataPoints.volume.push({ x: viewport.maximum, y: undefined })
    dataPoints.totals.push({ x: viewport.maximum, y: undefined })
    dataPoints.rate.push({ x: viewport.maximum, y: undefined })

    const end = new Date()

    const executionTime = (end.getTime() - start.getTime()) / 1000

    if (executionTime > 0 && false) {
      console.log({ executionTime, dataPoints })
    }

    return { dataPoints, totalVolume, totalVolumeInAcreFoot }
  }

  const flowVolume = {
    name: 'Volume',
    type: 'column',
    axisYType: 'secondary',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'blue',
    visible: showVolume,
    fillOpacity: 0.4,
    dataPoints: showVolume ? dataPoints.volume : [{ x: 0, y: 0 }],
  }

  const flowRate = {
    name: 'Flow Rate',
    axisYType: 'primary',
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'red',
    visible: showRate,
    fillOpacity: 0.1,
    dataPoints: showRate ? dataPoints.rate : [{ x: 0, y: 0 }],
  }

  const flowTotals = {
    name: 'Flow Total',
    axisYType: 'secondary',
    type: 'area',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'lightgreen',
    visible: showTotals,
    fillOpacity: 0.4,
    dataPoints: showTotals ? dataPoints.totals : [{ x: 0, y: 0 }],
  }

  const maxVolumeTotal = Math.max(showVolume ? maxVolume : 0, showTotals ? maxTotals : 0) || 10

  const maxVolumeRateTotal = Math.max(showRate ? maxRate : 0) || 10

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_FlowMeter`,
    title: {
      text: 'Flow Meter',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    willReadFrequently: true,
    dataPointWidth: 4,
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          const name = e.entries[i].dataSeries.name
          const precision = name === 'Flow Rate' ? settings.volumeRate.precision : settings.volume.precision
          const value = e.entries[i].dataPoint.y
          const color = e.entries[i].dataSeries.color
          const units =
            e.entries[i].dataSeries.name === 'Flow Rate'
              ? volumeRateConversions[settings.volumeRate.unitId]
              : volumeConversions[settings.volume.unitId]

          content += `<span style="color:${color}">${name}:</span> <strong> ${
            value !== undefined ? value.toFixed(precision) : ''
          } ${value !== undefined ? units : ''}</strong>`

          // Add velocity (ft/s) for Flow Rate
          if (name === 'Flow Rate' && value !== undefined && diameter > 0) {
            const velocity = (value * 0.002228) / (Math.PI * Math.pow(diameter / 24, 2))
            content += ` <span style="color:blue">(${velocity.toFixed(1)} ft/s)</span>`
          }

          content += '<br/>'
        }

        setX(e.entries[0].dataPoint.x)
        setY(e.entries[0].dataPoint.y)

        return content
      },
    },

    // subtitles: [
    //   {
    //     text: isLoading ? null : hasReadings ? null : 'RIC has no data for this time period',
    //     backgroundColor: 'red',
    //     fontColor: 'white',
    //     //Uncomment properties below to see how they behave
    //     margin: 10,
    //     padding: 15,
    //     cornerRadius: 5,
    //     fontSize: 20,
    //     fontWeight: 'bold',
    //     verticalAlign: 'top',
    //     dockInsidePlotArea: true,
    //   },
    // ],

    axisX: [
      {
        labelFormatter: function (e: any) {
          const date = new Date(e.value)
          const day = date.getDay()
          const dayChanged = trackDay !== day
          trackDay = day

          return formatDateForChart(date, dayChanged)
        },
        viewportMinimum: viewport?.minimum || null,
        viewportMaximum: viewport?.maximum || null,
        // labelAngle: -90,
        stripLines: [
          ...selectXRange.map((value) => {
            return {
              value,
              color: 'red',
              labelPlacement: 'outside',
            }
          }),

          selectXRange.length === 2 && {
            startValue: selectXRange[0],
            endValue: selectXRange[1],
            color: '#d8d8d8',
          },
        ],
      },
    ],
    axisY: [
      {
        includeZero: false,
        title: `Flow Rate (${volumeRateConversions === null ? '' : volumeRateConversions[settings.volumeRate.unitId]})`,
        suffix: '',
        minimum: 0,
        maximum: maxVolumeRateTotal * 2.5,
        titleFontSize,
        labelFontSize,
        dataPointWidth: 500,
      },
    ],
    axisY2: [
      {
        includeZero: false,
        title: `Volume (${volumeConversions === null ? '' : volumeConversions[settings.volume.unitId]})`,
        suffix: '',
        minimum: 0,
        maximum: maxVolumeTotal * 1.3,
        titleFontSize,
        labelFontSize,
        margin: 10,
        dataPointWidth: 500,
      },
    ],
    data: [flowRate, flowVolume, flowTotals],
    legend: {
      itemclick: (e: any) => {
        switch (e.dataSeries.name) {
          case 'Volume':
            setShowVolume(!showVolume)
            break
          case 'Flow Rate':
            setShowRate(!showRate)
            break
          case 'Flow Total':
            setShowTotals(!showTotals)
            break
        }
      },
    },
  }

  function checkForReadings() {
    return (
      sensorReadings &&
      Object.values(sensorReadings).filter(
        (data: any) =>
          Object.keys(data.readings).filter((timestamp) => {
            const ts = Number(timestamp) * 1000
            return ts >= viewport.minimum && ts <= viewport.maximum
          }).length > 0
      ).length > 0
    )
  }

  function getLastReadingTimestamp(): number {
    if (!hasReadings || !sensorReadings) return 0
    const timestamps = Object.keys(sensorReadings[0].readings)
    const ts = Number(timestamps[timestamps.length - 1])
    return ts
  }

  const currentRate = dataPoints.rate?.filter((data) => data.y !== undefined).slice(-1)[0]?.y

  // const title = `${device.config.device.name}: ${device.config.site.name || '-'}`
  const title = 'Flow Meter'

  return (
    <div className={tailwind.container}>
      <CollapsibleSection
        title={title}
        // mode={mode}
        // setMode={setMode}
        open={true}
        isLoading={isLoading}
      >
        <div className={tailwind.collapsibleSection}>
          <FlowMeterChartSubHeader
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorProduct={sensorProduct}
            sensorMetadata={sensorReadings ? sensorReadings.map((data: any) => data.metadata) : []}
            currentRate={currentRate || 0}
            totalVolume={totalVolume || 0}
            totalVolumeInAcreFoot={totalVolumeInAcreFoot || 0}
            lastReading={getLastReadingTimestamp()}
            settings={settings}
          />
        </div>
      </CollapsibleSection>
      <CanvasJSChart ref={ref} options={options} />
    </div>
  )
}

const tailwind = {
  container: 'mb-4',
  collapsibleSection: 'pb-2',
}
